import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _get from 'lodash/get';
import _debounce from 'lodash/debounce';
import classNames from 'classnames';

import NavbarItem from './navbar-item';
import Dropdown from './dropdown';

class Navbar extends Component {

    constructor(props) {

        super(props);
        this.state = {
            dropdownOpen: false,
            visibleItemsMap: [...Object.keys(this.props.items)],
            hiddenItemsMap: [],
            offsetDropdown: 44,
        };
        this.items = this.props.items;
        this.navContainerRef = React.createRef();
        this.listRef = React.createRef();
        this.dropdownRef = React.createRef();
        this.itemsRefs = [];
        this._btnHandler = this._btnHandler.bind(this);
        this._renderVisibleListItem = this._renderListItem.bind(this, false);
        this._renderHiddenListItem = this._renderListItem.bind(this, true);
        this._updateListItems = this._updateListItems.bind(this);

    }

    componentDidMount() {

        if (this._isThereOverflow(_get(this.listRef, 'current'))) {

            this._updateListItems();

        }

        window.addEventListener('resize', _debounce(this._updateListItems, 250));

    }

    componentWillUnmount() {

        window.removeEventListener('resize', _debounce(this._updateListItems, 250));

    }

    _updateListItems() {

        const isSmallScreen = window.matchMedia('(max-width: 767px)').matches;
        const isTouchDevice = 'ontouchstart' in document.documentElement;
        const wrapperWidth = _get(this.listRef, 'current.offsetWidth', 0);
        const moreBtnWidth = _get(this.dropdownRef, 'current.offsetWidth', 0);
        let isSpaceAvailable = true;
        let availableSpace = wrapperWidth - moreBtnWidth;

        const visibilityReducer = ([
            visibleItemsMap,
            hiddenItemsMap,
        ], item, key, src) => {
            const itemWidth = this._getInlineElementWidth(item.current);
            // If it's the last iteration, we should not include the dropdown button in the available space.
            if (src.length - 1 === key) {

                availableSpace = availableSpace + moreBtnWidth;

            }

            if (isSpaceAvailable && (availableSpace - itemWidth) >= 0) {

                visibleItemsMap.push(key);
                availableSpace = availableSpace - itemWidth;

            } else {

                hiddenItemsMap.push(key);
                isSpaceAvailable = false;

            }
            return [
                visibleItemsMap,
                hiddenItemsMap,
            ];

        };

        const [
            visibleItemsMap,
            hiddenItemsMap,
        ] = this.itemsRefs.reduce(visibilityReducer, [
            [],
            [],
        ]);

        this.setState({
            isSmallScreen,
            visibleItemsMap,
            hiddenItemsMap,
            isTouchDevice
        });

    }

    _getInlineElementWidth(item) {
        if (!item) {

            return 0;

        }

        return item.offsetWidth;

    }

    _getOrCreateRef(id) {

        if (!this.itemsRefs.hasOwnProperty(id)) {

            this.itemsRefs[id] = React.createRef();

        }

        return this.itemsRefs[id];

    }

    _isThereOverflow(e) {

        return e ? e.offsetWidth < e.scrollWidth : false;

    }

    _btnHandler(event, isOpen) {
        if (!event) {
            return;
        }

        const topOffset = this.navContainerRef.current.getBoundingClientRect().bottom;

        if (!isOpen) {

            // Just tracking the dropdown expansion
            this.props.onSubNavDropDownClick(event);

        }

        return this.setState({
            dropdownOpen: isOpen,
            offsetDropdown: topOffset,
        });

    }

    _renderListItem(isHiddenElement, key) {

        const item = this.items[key];

        const {
            ...otherProps
        } = item.props;

        const classNames = [];

        //Attach the analytics tracking to each item in the subNav
        const analyticsTracker = (e) => this.props.onSubNavItemClick(e, key);

        if (classNames.indexOf('navbar__link') === -1) {

            classNames.push('navbar__link');

        }

        const props = {
            className: classNames.join(' '),
            onClick: analyticsTracker,
            ...otherProps,
        };

        const { isSmallScreen, isTouchDevice, dropdownOpen, offsetDropdown } = this.state;

        if (isHiddenElement && !dropdownOpen) {

            props.tabIndex = -1;

        }

        if (item.menuItems.length > 0) {

            const subMenuItems = item.menuItems.map((subitem, index) => {

                return (
                    <NavbarItem
                        item={subitem}
                        itemsBasePath={this.props.itemsBasePath}
                    />
                )
            })
            // using classNames in this fn breaks things
            const dropdownClass = `navbar__dropdown${isHiddenElement ? ' navbar__dropdown--flyout' : ' navbar__dropdown--submenu'}`;

            return (
                <Dropdown 
                    aria-label={item.text}
                    buttonData={{
                        ariaLabel: item.text, text: item.text
                    }}
                    className={dropdownClass}
                    clickHandler={this._btnHandler}
                    offsetDropdown={offsetDropdown}
                    itemData={{ item, path: this.props.itemsBasePath }}
                    items={subMenuItems}
                    isButton={isHiddenElement}
                    isSubmenuItem={isHiddenElement}
                    isSmallScreen={isSmallScreen}
                    isTouchDevice={isTouchDevice}
                    key={key}
                    ref={this._getOrCreateRef(key)}
                />
            )
        }

        return (
                <NavbarItem
                    item={item}
                    itemsBasePath={this.props.itemsBasePath}
                    ref={this._getOrCreateRef(key)}
                    key={key}
                />
            );

    }

    render() {
        const {
            searchComponent,
            dropdownLabel,
        } = this.props;
        const {
            visibleItemsMap,
            hiddenItemsMap,
            offsetDropdown,
            isSmallScreen,
            isTouchDevice
        } = this.state;

        const items = visibleItemsMap.map(this._renderVisibleListItem);
        const hiddenItems = hiddenItemsMap.map(this._renderHiddenListItem);
        const dropdownClass = classNames({
            'navbar__item': true,
            'navbar__dropdown': true,
            'navbar__dropdown--hidden': !hiddenItemsMap.length
        });

        return (
            <section className='navbar'>
                <nav className='navbar__container' ref={this.navContainerRef}>
                    <ul className='navbar__items' ref={this.listRef}>
                        {items}
                        <Dropdown
                            className={dropdownClass}
                            ref={this.dropdownRef}
                            clickHandler={this._btnHandler}
                            items={hiddenItems}
                            isSmallScreen={isSmallScreen}
                            isTouchDevice={isTouchDevice}
                            isButton={true}
                            buttonData={{ ariaLabel: 'More channels', text: dropdownLabel }}
                            offsetDropdown={offsetDropdown}
                        />
                    </ul>
                    {!!searchComponent && searchComponent}
                </nav>
            </section>
        );

    }

};

Navbar.propTypes = {
    searchComponent: PropTypes.object,
    items: PropTypes.arrayOf(PropTypes.node),
    subMenuItems: PropTypes.arrayOf(PropTypes.node),
    dropdownLabel: PropTypes.string,
    onSubNavItemClick: PropTypes.func,
    onSubNavDropDownClick: PropTypes.func,
};

Navbar.defaultProps = {
    searchComponent: null,
    items: [],
    subMenuItems: [],
    dropdownLabel: 'More Channels',
    onSubNavItemClick: () => null,
    onSubNavDropDownClick: () => null,
};

export default Navbar;
